import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Star, Zap } from 'lucide-react';

const Pricing = () => {
  const plans = [
    {
      name: 'Starter',
      price: 79.99,
      period: '30 days',
      description: 'A budget-friendly option for small streaming teams or individuals just getting started.',
      features: [
        'Up to 2 competitions',
        'Up to 10 teams',
        'Up to 200 players',
        'Up to 1 custom skin',
        'Up to 2 sessions per week',
      ],
      notIncluded: [
        // 'Custom branding',
        // 'Advanced analytics',
        // 'Priority support'
      ],
      popular: false,
      cta: 'Let\'s Go'
    },
    {
      name: 'Pro',
      price: 249.99,
      period: '30 days',
      description: 'Built for growing production teams or local media companies.',
      features: [
        'Up to 5 competitions',
        'Up to 40 teams',
        'Up to 500 players',
        'Up to 4 custom skins',
        'Up to 4 sessions per week',
      ],
      notIncluded: [
        // 'White-label solution'
      ],
      popular: true,
      cta: 'Let\'s Go!'
    },
    {
      name: 'Elite',
      price: null,
      period: '30 days',
      description: 'Designed for advanced broadcasters and professional media operations.',
      features: [
        'Unlimited competitions',
        'Unlimited teams',
        'Unlimited players',
        'Unlimited custom skins',
        'Up to 15 sessions per week',
      ],
      notIncluded: [],
      popular: false,
      cta: 'Contact Sales'
    }
  ];

  return (
    <section id="pricing" className="py-20 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-blue-900/20 to-slate-900"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Choose Your
            <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Perfect Plan
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative bg-slate-800/50 backdrop-blur-sm border rounded-2xl p-8 hover:bg-slate-700/50 transition-all duration-300 ${
                plan.popular 
                  ? 'border-blue-500 ring-2 ring-blue-500/20 scale-105' 
                  : 'border-slate-700/50 hover:border-slate-600/50'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Star className="h-3 w-3 fill-current" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 mb-4">{plan.description}</p>
                {plan.price && (
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-4xl font-bold text-white">{plan.price} PLN</span>
                    <span className="text-slate-400">/{plan.period}</span>
                  </div>
                )}
              </div>

              {/* Features List */}
              <div className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
                {plan.notIncluded.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3 opacity-50">
                    <X className="h-5 w-5 text-slate-500 flex-shrink-0" />
                    <span className="text-slate-500 line-through">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.button
                className={`w-full py-4 rounded-xl font-semibold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:from-blue-700 hover:to-cyan-600 shadow-lg hover:shadow-xl'
                    : 'bg-slate-700 text-white hover:bg-slate-600 border border-slate-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  if (plan.price) {
                    window.open('https://portal.sportstreamkit.com/register', '_blank');
                  }
                  else {
                    window.open('mailto:info@sportstreamkit.com');
                  }
                }}
              >
                {plan.cta}
              </motion.button>

              {/* Plan Benefits */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-center space-x-2 text-sm text-slate-400">
                  <Zap className="h-4 w-4" />
                  <span>One-off payment</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Money Back Guarantee */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">30-Day Money-Back Guarantee</h3>
            <p className="text-slate-300 mb-6">
              Not satisfied? Get a full refund within 30 days, no questions asked. We're confident you'll love Sport Stream Kit.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-slate-400">
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>No setup fees</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-400" />
                <span>24/7 support</span>
              </div>
            </div>
          </div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;